// src/ui-home.ts
import type { RoomLaunchConfig } from './room-state';
import { clearTeslaWithMeBrowserState } from './browser-state';
import { CAR_COLORS, isCarColor } from './car-colors';
import { CAR_SHAPES, DEFAULT_CAR_SHAPE, carMarkerSvg, isCarShape, type CarShape } from './car-shapes';
import { readInviteFromUrl, shareRoom } from './share-room';
import { generateRoomCode, validateRoomCode } from './words';

const STORAGE_KEY = 'teslawithme:savedRoom';
const DISPLAY_NAME_KEY = 'teslawithme:displayName';
const CAR_COLOR_KEY = 'teslawithme:carColor';
const CAR_SHAPE_KEY = 'teslawithme:carShape';

export function renderHome(
  container: HTMLElement,
  onCreateRoom: (room: RoomLaunchConfig) => void,
  onJoinRoom: (room: RoomLaunchConfig) => void,
  onOpenGeoDiagnostic: () => void,
): void {
  const invite = readInviteFromUrl();
  const hasInvite = Boolean(invite.roomCode);
  const savedCode = invite.roomCode || (localStorage.getItem(STORAGE_KEY) ?? '');
  const savedCheckWord = invite.checkWord;
  const savedName = localStorage.getItem(DISPLAY_NAME_KEY) ?? '';
  const savedColorRaw = localStorage.getItem(CAR_COLOR_KEY);
  let chosenColor: string | undefined = isCarColor(savedColorRaw) ? savedColorRaw : undefined;
  const savedShapeRaw = localStorage.getItem(CAR_SHAPE_KEY);
  let chosenShape: CarShape = isCarShape(savedShapeRaw) ? savedShapeRaw : DEFAULT_CAR_SHAPE;

  const headerHtml = `
      <!-- Header -->
      <div class="text-center w-full max-w-md pt-4">
        <div class="flex items-center justify-center gap-2">
          <h1 class="text-[22px] sm:text-3xl font-light text-white uppercase tracking-[0.32em] pl-[0.32em] whitespace-nowrap">Tesla With Me</h1>
          <button id="info-btn" aria-label="About"
            class="text-tesla-muted hover:text-white transition-colors mt-1 p-1">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24"
              fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/>
              <line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
          </button>
        </div>
        <p class="text-tesla-muted mt-2 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] sm:tracking-[0.28em] px-2 sm:whitespace-nowrap">Convoy tracker for open roads. No accounts. No BS.</p>
        <svg viewBox="0 0 400 72" class="w-full max-w-md mt-5 select-none" aria-hidden="true" fill="none">
          <rect x="0" y="46" width="400" height="14" rx="7" fill="#141517"/>
          <line x1="14" y1="53" x2="386" y2="53" stroke="#2c2d30" stroke-width="1.5" stroke-dasharray="10 14"/>
          <line x1="36" y1="40" x2="96" y2="40" stroke="#e6194b" stroke-width="2.5" stroke-linecap="round" opacity="0.3"/>
          <line x1="150" y1="40" x2="204" y2="40" stroke="#4d7dff" stroke-width="2.5" stroke-linecap="round" opacity="0.3"/>
          <line x1="258" y1="40" x2="308" y2="40" stroke="#e0e0e0" stroke-width="2.5" stroke-linecap="round" opacity="0.25"/>
          ${carGlyph(100, '#e6194b')}
          ${carGlyph(208, '#4d7dff')}
          ${carGlyph(312, '#e0e0e0')}
        </svg>
        <div id="info-panel" class="hidden mt-4 text-left text-sm text-white/80 bg-tesla-surface border border-tesla-border rounded-xl px-4 py-4 space-y-3">
          <p>Share your live location with friends during road trips, convoys, and meetups.
            Create a room, send the 3-word code, and everyone who joins appears on the same map.</p>
          <ol class="space-y-1 text-tesla-muted list-decimal list-inside">
            <li>Start a room and generate a 3-word code.</li>
            <li>Share the code with your group.</li>
            <li>Everyone allows location and shows up on the map.</li>
          </ol>
          <div class="border-t border-tesla-border pt-3 space-y-1.5">
            <p class="text-tesla-cyan font-semibold text-xs uppercase tracking-wider">Privacy</p>
            <p class="text-tesla-muted">Location and chat travel directly between your devices — our server only handles the initial connection handshake and never sees your messages or coordinates.</p>
            <p class="text-tesla-muted">Chat history is kept in the memory of connected peers for up to one hour so late joiners can catch up. It is never written to a database and disappears when everyone leaves the room.</p>
          </div>
        </div>
        <button id="geo-check-btn"
          class="mt-5 border border-white/15 text-tesla-muted hover:text-white rounded-full px-6 py-3 text-sm transition-colors active:opacity-75">
          Browser Location Check
        </button>
      </div>`;

  // Name + car color inputs. Rendered once: inside the Join card when the
  // user arrives via an invite link (identity is part of joining), or as a
  // standalone card on the normal landing (it serves create AND join).
  const identityFieldsHtml = `
        <label for="display-name" class="text-[11px] uppercase tracking-[0.2em] text-tesla-muted font-semibold">Your Name
          <span class="normal-case tracking-normal font-normal ml-1 opacity-70">· optional</span>
        </label>
        <input id="display-name" type="text"
          value="${escapeAttr(savedName)}"
          placeholder="e.g. Alex, Red Model 3…"
          class="mt-2.5 w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-base text-white placeholder-tesla-muted focus:outline-none focus:border-tesla-cyan transition-colors"
          autocomplete="off" autocorrect="off" autocapitalize="words" spellcheck="false"
          maxlength="24"/>
        <p class="text-tesla-muted text-xs mt-1.5">Shows in chat instead of your connection ID.</p>

        <div class="text-[11px] uppercase tracking-[0.2em] text-tesla-muted font-semibold mt-5">Your Car Color
          <span class="normal-case tracking-normal font-normal ml-1 opacity-70">· optional</span>
        </div>
        <div id="car-color-row" class="mt-2 flex flex-wrap gap-2 items-center">
          <button type="button" data-color=""
            class="car-color-btn w-12 h-12 rounded-full border-2 border-tesla-border bg-black text-tesla-muted text-xs font-semibold active:opacity-75"
            title="Automatic color" aria-label="Automatic color">Auto</button>
          ${CAR_COLORS.map(c => `
          <button type="button" data-color="${c.value}"
            class="car-color-btn w-12 h-12 rounded-full border-2 border-tesla-border active:opacity-75"
            style="background:${c.value}" title="${c.name}" aria-label="${c.name}"></button>`).join('')}
        </div>
        <p class="text-tesla-muted text-xs mt-1.5">Everyone sees your car and trail in this color on the map.</p>

        <div class="text-[11px] uppercase tracking-[0.2em] text-tesla-muted font-semibold mt-5">Your Car</div>
        <div id="car-shape-row" class="mt-2 flex flex-wrap gap-2 items-center">
          ${CAR_SHAPES.map(shape => `
          <button type="button" data-shape="${shape.id}"
            class="car-shape-btn flex flex-col items-center justify-center gap-0.5 w-14 rounded-xl border-2 border-tesla-border text-white active:opacity-75"
            title="${shape.name}" aria-label="${shape.name}">
            <span class="block w-8 h-8">${carMarkerSvg(shape.id, '#ffffff')}</span>
            <span class="text-[9px] leading-none text-tesla-muted">${shape.short}</span>
          </button>`).join('')}
        </div>`;

  const nameCardHtml = `
      <!-- Display name -->
      <div class="w-full max-w-md bg-tesla-surface border border-white/[0.08] rounded-2xl px-6 py-5">${identityFieldsHtml}
      </div>`;


  const inviteBannerHtml = hasInvite ? `
            <div class="w-full max-w-md bg-tesla-surface border border-tesla-cyan/60 rounded-2xl px-5 py-4" id="invite-banner">
        <div class="text-tesla-cyan font-semibold text-base">Invite Ready</div>
        <div class="text-white text-sm mt-1">This link already filled your room details.</div>
        <div class="text-white font-bold tracking-widest mt-2">${escapeAttr(savedCode)}</div>
        ${savedCheckWord ? `<div class="text-tesla-muted text-sm mt-1">Check word: ${escapeAttr(savedCheckWord)}</div>` : ''}
      </div>` : '';

  const createCardHtml = `
      <!-- Create Room -->
      <div class="w-full max-w-md bg-tesla-surface border border-white/[0.08] rounded-2xl p-6">
        <h2 class="text-[11px] uppercase tracking-[0.2em] text-tesla-muted font-semibold mb-4">Start a Room</h2>
        <div id="generated-code" class="text-2xl font-semibold text-white text-center py-5 tracking-[0.18em] cursor-pointer select-all border border-white/10 rounded-xl bg-black mb-3"
          title="Tap to regenerate">
          ${generateRoomCode()}
        </div>
        <button id="regen-btn"
          class="w-full border border-white/15 text-tesla-muted hover:text-white rounded-full py-3 mb-4 text-base transition-colors active:opacity-75">
          Regenerate Code ↻
        </button>
        <button id="create-btn"
          class="w-full bg-white text-black font-semibold rounded-full py-4 text-lg active:opacity-75">
          Create Room
        </button>
        <input id="create-check-word" type="text"
          placeholder="Optional check word (X handle, nickname, etc.)"
          class="w-full mt-4 bg-black border border-white/15 rounded-xl px-4 py-3 text-base text-white placeholder-tesla-muted focus:outline-none focus:border-tesla-cyan transition-colors" />
        <button id="share-btn"
          class="w-full mt-3 bg-white/10 text-white rounded-full py-3 text-base active:opacity-75">
          Share Invite
        </button>
        <div id="share-feedback" class="text-tesla-muted text-sm text-center mt-3 min-h-[20px]"></div>
        <p class="text-tesla-muted text-sm text-center mt-1">
          Best when one person creates the room and shares the code before everyone joins.
        </p>
      </div>`;

  const joinCardHtml = `
      <!-- Join Room -->
      <div class="w-full max-w-md bg-tesla-surface border ${hasInvite ? 'border-tesla-cyan/40' : 'border-white/[0.08]'} rounded-2xl p-6">
        <h2 class="text-[11px] uppercase tracking-[0.2em] text-tesla-muted font-semibold mb-4">${hasInvite ? 'Join This Invite' : 'Join a Room'}</h2>
        <input id="join-input" type="text"
          value="${escapeAttr(savedCode)}"
          placeholder="ENTER ROOM CODE"
          class="w-full bg-black border border-white/15 rounded-xl px-4 py-4 text-xl text-white text-center uppercase tracking-[0.18em] placeholder-tesla-muted focus:outline-none focus:border-tesla-cyan transition-colors mb-3 min-h-[64px]"
          autocomplete="off" autocorrect="off" autocapitalize="characters" spellcheck="false"/>
        <input id="join-check-word" type="text"
          value="${escapeAttr(savedCheckWord)}"
          placeholder="Optional check word"
          class="w-full bg-black border border-white/15 rounded-xl px-4 py-3 text-base text-white placeholder-tesla-muted focus:outline-none focus:border-tesla-cyan transition-colors mb-3"
          autocomplete="off" autocorrect="off" autocapitalize="off" spellcheck="false"/>
        ${hasInvite ? `
        <div class="border-t border-white/[0.08] mt-4 pt-4 mb-5">${identityFieldsHtml}
        </div>` : ''}
        <div id="join-error" class="text-red-400 text-sm text-center mb-2 hidden"></div>
        <button id="join-btn"
          class="w-full bg-tesla-cyan text-white font-semibold rounded-full py-4 text-lg active:opacity-75">
          ${hasInvite ? 'Join This Room' : 'Join Room'}
        </button>
        <p class="text-tesla-muted text-sm text-center mt-3">
          ${hasInvite
            ? 'Your room code is prefilled from the invite link. Allow location and jump in.'
            : 'Enter the 3-word code exactly as it was shared with you.'}
        </p>
      </div>`;

  // Invite links land people who should simply join — put the invite + Join
  // card first and tuck room creation behind a quiet expander at the bottom.
  const createSectionHtml = hasInvite
    ? `
      <button id="show-create"
        class="w-full max-w-md border border-white/15 text-tesla-muted hover:text-white rounded-full py-3 text-base transition-colors active:opacity-75">
        Start a new room instead
      </button>
      <div id="create-section" class="hidden w-full max-w-md">${createCardHtml}</div>`
    : createCardHtml;

  container.innerHTML = `
    <div class="flex flex-col items-center min-h-screen bg-tesla-bg px-6 py-8 gap-6">
${headerHtml}
${hasInvite
      ? inviteBannerHtml + joinCardHtml + createSectionHtml
      : nameCardHtml + createSectionHtml + joinCardHtml}
      <!-- Long-lived group toggle -->
      <div class="w-full max-w-md flex items-center justify-between bg-tesla-surface border border-white/[0.08] rounded-2xl px-6 py-4">
        <div>
          <div class="text-white font-medium">Remember Room</div>
          <div class="text-tesla-muted text-sm">Save the code in this browser for next trip</div>
        </div>
        <label class="relative inline-block w-14 h-8">
          <input id="remember-toggle" type="checkbox" class="sr-only" ${savedCode ? 'checked' : ''}/>
          <span class="block w-full h-full bg-tesla-border rounded-full cursor-pointer transition-colors" id="toggle-track"></span>
          <span class="absolute top-1 left-1 w-6 h-6 bg-white rounded-full transition-transform" id="toggle-thumb"></span>
        </label>
      </div>

      <footer class="w-full max-w-md border-t border-white/[0.08] pt-5 text-center">
        <button id="reset-browser-state-btn"
          class="w-full border border-tesla-red/40 text-tesla-red/90 rounded-full px-4 py-3 text-sm active:opacity-75">
          Reset App Storage
        </button>
        <div id="reset-browser-state-feedback" class="mt-3 text-xs text-tesla-muted min-h-[18px]"></div>
      </footer>

    </div>
  `;

  const showCreateBtn = container.querySelector('#show-create') as HTMLButtonElement | null;
  showCreateBtn?.addEventListener('click', () => {
    container.querySelector('#create-section')?.classList.remove('hidden');
    showCreateBtn.classList.add('hidden');
  });

  const infoBtn = container.querySelector('#info-btn') as HTMLButtonElement;
  const infoPanel = container.querySelector('#info-panel') as HTMLElement;
  infoBtn.addEventListener('click', () => infoPanel.classList.toggle('hidden'));
  const geoCheckBtn = container.querySelector('#geo-check-btn') as HTMLButtonElement;
  geoCheckBtn.addEventListener('click', onOpenGeoDiagnostic);
  const resetBrowserStateBtn = container.querySelector('#reset-browser-state-btn') as HTMLButtonElement;
  const resetBrowserStateFeedback = container.querySelector('#reset-browser-state-feedback') as HTMLElement;
  resetBrowserStateBtn.addEventListener('click', () => {
    clearTeslaWithMeBrowserState();
    resetBrowserStateFeedback.textContent =
      'TeslaWithMe storage cleared. If location still does not prompt, reset Location for this site in your browser settings.';
    window.setTimeout(() => {
      window.location.href = `${window.location.pathname}?screen=geo-check`;
    }, 250);
  });

  const displayNameInput = container.querySelector('#display-name') as HTMLInputElement;
  const colorButtons = container.querySelectorAll<HTMLButtonElement>('.car-color-btn');
  function updateColorUI(): void {
    colorButtons.forEach(btn => {
      const selected = (btn.dataset.color || undefined) === chosenColor;
      btn.classList.toggle('border-tesla-cyan', selected);
      btn.classList.toggle('border-tesla-border', !selected);
      btn.style.transform = selected ? 'scale(1.15)' : '';
    });
  }
  updateColorUI();
  const shapeButtons = container.querySelectorAll<HTMLButtonElement>('.car-shape-btn');
  function updateShapeUI(): void {
    shapeButtons.forEach(btn => {
      const selected = btn.dataset.shape === chosenShape;
      btn.classList.toggle('border-tesla-cyan', selected);
      btn.classList.toggle('border-tesla-border', !selected);
    });
  }
  updateShapeUI();
  shapeButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const next = btn.dataset.shape;
      if (!isCarShape(next)) return;
      chosenShape = next;
      localStorage.setItem(CAR_SHAPE_KEY, next);
      updateShapeUI();
    });
  });
  colorButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      chosenColor = btn.dataset.color || undefined;
      if (chosenColor) localStorage.setItem(CAR_COLOR_KEY, chosenColor);
      else localStorage.removeItem(CAR_COLOR_KEY);
      updateColorUI();
    });
  });
  const generatedCodeEl = container.querySelector('#generated-code') as HTMLElement;
  const regenBtn = container.querySelector('#regen-btn') as HTMLButtonElement;
  const createBtn = container.querySelector('#create-btn') as HTMLButtonElement;
  const createCheckWord = container.querySelector('#create-check-word') as HTMLInputElement;
  const shareBtn = container.querySelector('#share-btn') as HTMLButtonElement;
  const shareFeedback = container.querySelector('#share-feedback') as HTMLElement;
  const joinInput = container.querySelector('#join-input') as HTMLInputElement;
  const joinCheckWord = container.querySelector('#join-check-word') as HTMLInputElement;
  const joinBtn = container.querySelector('#join-btn') as HTMLButtonElement;
  const joinError = container.querySelector('#join-error') as HTMLElement;
  const rememberToggle = container.querySelector('#remember-toggle') as HTMLInputElement;
  const toggleTrack = container.querySelector('#toggle-track') as HTMLElement;
  const toggleThumb = container.querySelector('#toggle-thumb') as HTMLElement;

  function updateToggleUI(): void {
    if (rememberToggle.checked) {
      toggleTrack.classList.add('bg-tesla-cyan');
      toggleTrack.classList.remove('bg-tesla-border');
      toggleThumb.style.transform = 'translateX(24px)';
    } else {
      toggleTrack.classList.remove('bg-tesla-cyan');
      toggleTrack.classList.add('bg-tesla-border');
      toggleThumb.style.transform = 'translateX(0)';
    }
  }
  updateToggleUI();
  rememberToggle.addEventListener('change', updateToggleUI);

  regenBtn.addEventListener('click', () => {
    generatedCodeEl.textContent = generateRoomCode();
  });
  generatedCodeEl.addEventListener('click', () => {
    generatedCodeEl.textContent = generateRoomCode();
  });

  shareBtn.addEventListener('click', async () => {
    const code = generatedCodeEl.textContent!.trim();
    const result = await shareRoom(code, { checkWord: createCheckWord.value.trim() });
    if (result === 'shared') {
      shareFeedback.textContent = 'Invite opened in your share sheet.';
    } else if (result === 'copied') {
      shareFeedback.textContent = 'Invite copied. Paste it into iMessage, SMS, or anywhere else.';
    } else if (result === 'unsupported') {
      shareFeedback.textContent = 'Sharing is not available in this browser.';
    } else {
      shareFeedback.textContent = '';
    }
  });

  // Auto-uppercase the join input
  joinInput.addEventListener('input', () => {
    const pos = joinInput.selectionStart;
    joinInput.value = joinInput.value.toUpperCase();
    joinInput.setSelectionRange(pos, pos);
    joinError.classList.add('hidden');
  });

  createBtn.addEventListener('click', () => {
    const code = generatedCodeEl.textContent!.trim();
    const checkWord = normalizeCheckWord(createCheckWord.value);
    const displayName = displayNameInput.value.trim() || undefined;
    if (displayName) localStorage.setItem(DISPLAY_NAME_KEY, displayName);
    else localStorage.removeItem(DISPLAY_NAME_KEY);
    if (rememberToggle.checked) localStorage.setItem(STORAGE_KEY, code);
    else localStorage.removeItem(STORAGE_KEY);
    onCreateRoom({ code, checkWord, isHost: true, displayName, carColor: chosenColor, carShape: chosenShape });
  });

  joinBtn.addEventListener('click', () => {
    const raw = joinInput.value.trim();
    const checkWord = normalizeCheckWord(joinCheckWord.value);
    const displayName = displayNameInput.value.trim() || undefined;
    if (!validateRoomCode(raw)) {
      joinError.textContent =
        "That code doesn't look right — try 3 words from the list, like ACID AGILE ALARM.";
      joinError.classList.remove('hidden');
      return;
    }
    if (displayName) localStorage.setItem(DISPLAY_NAME_KEY, displayName);
    else localStorage.removeItem(DISPLAY_NAME_KEY);
    if (rememberToggle.checked) localStorage.setItem(STORAGE_KEY, raw.toUpperCase());
    else localStorage.removeItem(STORAGE_KEY);
    onJoinRoom({ code: raw.toUpperCase(), checkWord, isHost: false, displayName, carColor: chosenColor, carShape: chosenShape });
  });
}

function normalizeCheckWord(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed || undefined;
}

function escapeAttr(s: string): string {
  return s.replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/** Small car silhouette for the hero convoy graphic (matches the map icon). */
function carGlyph(x: number, color: string): string {
  return `<g transform="translate(${x} 26)">
    <rect x="0" y="8" width="30" height="12" rx="4" fill="${color}" opacity="0.95"/>
    <rect x="5" y="2" width="18" height="9" rx="3" fill="${color}"/>
    <circle cx="7" cy="21" r="3.4" fill="#26272a"/>
    <circle cx="23" cy="21" r="3.4" fill="#26272a"/>
    <circle cx="7" cy="21" r="1.5" fill="#6a6d72"/>
    <circle cx="23" cy="21" r="1.5" fill="#6a6d72"/>
  </g>`;
}
